/**
 * Markdown bugfix: gpt-5.x (and other reasoning models) routinely emit summary
 * paragraphs indented after numbered lists, e.g.
 *
 *   50. 06/05 — Decathlon — -R$ 58,57
 *
 *       No total do período, foram R$ 20.317,30 recebidos...
 *
 *       O que chama atenção é que o mês não parece pesado por "miudeza"...
 *
 * Per CommonMark, the indented blocks become **list-item continuation
 * paragraphs** of item 50. ReactMarkdown then renders them inside the last
 * `<li>`, which inherits the list's `ml-4` + `list-outside` styles, so the
 * paragraphs appear visually nested under the list instead of as siblings.
 *
 * This preprocessor walks blank-line-separated blocks and, once it has seen a
 * list block, dedents any subsequent non-list / non-heading block that still
 * has leading whitespace. It also forces a blank line between a list item and
 * any immediately-following non-list, non-blank content so CommonMark's
 * lazy-continuation rule cannot absorb prose into the last item.
 *
 * Whitespace characters considered as indentation include space, tab, and the
 * Unicode no-break / figure / narrow no-break spaces (`\u00A0`, `\u2007`,
 * `\u202F`) which some models emit instead of plain spaces.
 *
 * Trade-off: a chat assistant that *intentionally* writes a continuation
 * paragraph attached to the last list item would also be dedented. In our
 * domain (financial summaries, narration) that pattern essentially never
 * occurs, and the visual bug is far more disruptive than the false-positive
 * cost. If a future tool relies on real continuation paragraphs, complement
 * this with a remark plugin operating on the AST.
 */

const INDENT_CHARS = '[ \\t\\u00A0\\u2007\\u202F]';
const LIST_LINE_RE = new RegExp(
  `^${INDENT_CHARS}{0,3}(?:\\d+[.)]|[-*+])${INDENT_CHARS}+`,
);
const HEADING_LINE_RE = new RegExp(
  `^${INDENT_CHARS}{0,3}#{1,6}${INDENT_CHARS}+`,
);
const FENCE_LINE_RE = new RegExp(`^${INDENT_CHARS}*(?:\`{3,}|~{3,})`);
const LEADING_INDENT_RE = new RegExp(`^${INDENT_CHARS}+`);
const BLANK_LINE_RE = new RegExp(`^${INDENT_CHARS}*$`);

const isListLine = (s: string) => LIST_LINE_RE.test(s);
const isHeadingLine = (s: string) => HEADING_LINE_RE.test(s);
const isFenceLine = (s: string) => FENCE_LINE_RE.test(s);
const isBlankLine = (s: string) => BLANK_LINE_RE.test(s);
const leadingIndentLen = (s: string) =>
  s.match(LEADING_INDENT_RE)?.[0].length ?? 0;

export function dedentParagraphsAfterLists(md: string): string {
  if (!md || !md.includes('\n')) return md;

  const lines = md.split('\n');
  const result: string[] = [];

  let afterList = false;
  let lastEmittedWasListLine = false;
  let inFence = false;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isFenceLine(line)) {
      inFence = !inFence;
      afterList = false;
      lastEmittedWasListLine = false;
      result.push(line);
      i++;
      continue;
    }

    if (inFence) {
      result.push(line);
      i++;
      continue;
    }

    if (isBlankLine(line)) {
      lastEmittedWasListLine = false;
      result.push(line);
      i++;
      continue;
    }

    const blockStart = i;
    while (i < lines.length && !isBlankLine(lines[i])) {
      i++;
    }
    const block = lines.slice(blockStart, i);
    const firstLine = block[0] ?? '';
    const blockIsList = isListLine(firstLine);
    const blockIsHeading = isHeadingLine(firstLine);

    if (blockIsList) {
      // Within a contiguous list block we may also need to break out: if a
      // non-list, non-indented line follows a list item without a blank line,
      // CommonMark treats it as lazy continuation of that item. Split the
      // block so the prose becomes a sibling paragraph.
      let consumed = 0;
      while (consumed < block.length) {
        const ln = block[consumed];
        if (isListLine(ln)) {
          result.push(ln);
          consumed++;
          continue;
        }
        // First non-list line inside the list block: if it has no leading
        // indentation, it's prose that the parser would absorb. Insert a
        // blank-line separator and let the outer loop reprocess the tail as
        // its own block (with afterList=true so it gets dedented if needed).
        if (leadingIndentLen(ln) === 0) {
          afterList = true;
          lastEmittedWasListLine = false;
          // Rewind the outer cursor to the tail so it picks up here.
          i = blockStart + consumed;
          result.push('');
          break;
        }
        // Otherwise the line is indented; let the regular block flow handle
        // it (e.g. legitimate continuation paragraph styling).
        result.push(ln);
        consumed++;
      }
      if (consumed >= block.length) {
        afterList = true;
        lastEmittedWasListLine = isListLine(block[block.length - 1] ?? '');
      }
      continue;
    }

    // Lazy-continuation guard for the *blank-line-separated* case: if the
    // previous emitted non-blank line is a list item, force a blank line
    // before this block. Without it, CommonMark would absorb this block as a
    // continuation paragraph of the last item.
    if (lastEmittedWasListLine && !blockIsHeading) {
      result.push('');
    }

    if (blockIsHeading) {
      afterList = false;
      lastEmittedWasListLine = false;
      result.push(...block);
      continue;
    }

    if (afterList) {
      const indents = block.map(leadingIndentLen);
      const minIndent = Math.min(...indents);
      if (minIndent > 0) {
        for (const l of block) result.push(l.slice(minIndent));
        lastEmittedWasListLine = false;
        continue;
      }
    }

    result.push(...block);
    lastEmittedWasListLine = false;
  }

  return result.join('\n');
}
